'use client'

import { type KeyboardEvent } from 'react'
import { EyeOff } from 'lucide-react'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { useTranslation } from '@/hooks/use-translation'

export interface JumpToColumnOption {
  fieldConfigurationId: string
  displayName: string
  /** True when the column is not in the current stage's view yet. Picking it
   *  will append it to the visible list before scrolling. */
  isHidden: boolean
}

interface JumpToColumnDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  columns: JumpToColumnOption[]
  onPick: (column: JumpToColumnOption) => void
}

/**
 * Command-palette dialog for scrolling the workflow grid horizontally to a
 * specific column. Relies on cmdk's built-in substring/score filtering — no
 * custom matcher needed.
 *
 * Implementation notes:
 *  - `CommandList` uses a FIXED height (not max-height) so the dialog doesn't
 *    visually jump as the user types and the list shrinks. Empty/short-list
 *    states keep the container tall and scroll area stable.
 *  - Tab navigates forward, Shift+Tab navigates backward — same as ArrowDown /
 *    ArrowUp. cmdk doesn't handle Tab natively, so we synthesize arrow-key events
 *    on the input; cmdk's internal keydown handler picks them up.
 *  - Selection highlight is strengthened via `data-[selected=true]:bg-blue-100`
 *    with a left-border accent — cmdk's default `bg-accent` is too subtle.
 */
export function JumpToColumnDialog({ isOpen, onOpenChange, columns, onPick }: JumpToColumnDialogProps) {
  const { t } = useTranslation()

  const visibleColumns = columns.filter(c => !c.isHidden)
  const hiddenColumns = columns.filter(c => c.isHidden)

  const handleSelect = (column: JumpToColumnOption) => {
    onPick(column)
    onOpenChange(false)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Tab') return
    e.preventDefault()
    const target = e.currentTarget
    const synth = new KeyboardEvent('keydown', {
      key: e.shiftKey ? 'ArrowUp' : 'ArrowDown',
      bubbles: true,
      cancelable: true,
    })
    target.dispatchEvent(synth)
  }

  const itemClass =
    "data-[selected=true]:bg-blue-100 data-[selected=true]:text-blue-900 data-[selected=true]:border-l-2 data-[selected=true]:border-blue-500 data-[selected=true]:pl-[6px]"

  return (
    <CommandDialog open={isOpen} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder={t('jumpToColumnPlaceholder')}
        onKeyDown={handleKeyDown}
      />
      <CommandList className="h-[300px] max-h-[300px]">
        <CommandEmpty>{t('noMatchingColumn')}</CommandEmpty>

        {visibleColumns.length > 0 && (
          <CommandGroup heading={t('visibleColumns')}>
            {visibleColumns.map((column) => (
              <CommandItem
                key={column.fieldConfigurationId}
                // cmdk filters on the `value` prop — use the display name so the user's
                // typed query matches what they see.
                value={column.displayName}
                onSelect={() => handleSelect(column)}
                className={itemClass}
              >
                {column.displayName}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {hiddenColumns.length > 0 && (
          <CommandGroup heading={t('hiddenColumns')}>
            {hiddenColumns.map((column) => (
              <CommandItem
                key={column.fieldConfigurationId}
                value={column.displayName}
                onSelect={() => handleSelect(column)}
                className={itemClass}
              >
                <EyeOff className="text-gray-400 !size-3.5" />
                <span className="flex-1">{column.displayName}</span>
                <span className="text-xs text-gray-500">{t('addToView')}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  )
}
